import React, { useCallback, useMemo, useState } from "react";
import { ExclamationTriangleIcon } from "@heroicons/react/24/solid";
import { LaunchableComputeOverview } from "components/Launchables/DeployRedesign/shared/configuration/compute/LaunchableComputeOverview";
import { LaunchableMultiComputeSelector } from "components/Launchables/DeployRedesign/shared/configuration/compute/LaunchableMultiComputeSelector";
import {
  GPUInstanceType,
  Location,
} from "types/InstanceTypes";
import { CreateFirewallRule } from "components/Environment/Create/ExposePorts";
import {
  getInstanceCloudCreds,
  getPrimaryInstanceCloudCredId,
} from "utils/cloudCreds";
import { regionToReadable } from "components/utils";

interface CloudTabContentProps {
  instanceTypeObject: GPUInstanceType | null;
  intendedInstanceType: string | null;
  intendedDiskStorage: string | null;
  instanceStorage: string | null;
  intendedCloudCredId: string | null;
  intendedFirewallRules: CreateFirewallRule[] | null;
  imageId?: string | null;
  summaryWarning?: React.ReactNode;
  onShowEditComputeModal: () => void;
  onCloudInstanceSelect: (
    instance: GPUInstanceType | null,
    storage: string,
  ) => void;
  onStorageValidityChange?: (isValid: boolean) => void;
  onIsOutOfStock?: (isOutOfStock: boolean) => void;
  onProviderDifferenceChange?: (isDifferent: boolean) => void;
  allInstancesAvailable: GPUInstanceType[];
  selectedLocation: string | null;
  onLocationChange: (location: string | null) => void;
}

export const getAvailableLocationsForInstance = (
  instanceType: GPUInstanceType | null,
  allInstances: GPUInstanceType[],
): Location[] => {
  if (!instanceType) return [];

  const selectedCloudCredentialId = getPrimaryInstanceCloudCredId(instanceType);
  const matchingInstances = selectedCloudCredentialId
    ? [instanceType, ...allInstances].filter(
        (instance, index, instances) =>
          instance.type === instanceType.type &&
          getPrimaryInstanceCloudCredId(instance) ===
            selectedCloudCredentialId &&
          instances.indexOf(instance) === index,
      )
    : [instanceType];
  const locationMap = new Map<string, Location>();

  matchingInstances.forEach((instance) => {
    const locationNames = instance.available_locations?.length
      ? instance.available_locations
      : instance.location
        ? [instance.location]
        : [];

    locationNames.forEach((locationName) => {
      if (locationMap.has(locationName)) return;

      const locationDetails = getInstanceCloudCreds(instance)
        .flatMap((cloudCred) => cloudCred.locations || [])
        .find((location) => location.name === locationName);
      locationMap.set(
        locationName,
        locationDetails || {
          name: locationName,
          description: locationName,
        },
      );
    });
  });

  return Array.from(locationMap.values()).sort((a, b) => {
    const aLabel = regionToReadable[a.name]?.city || a.description || a.name;
    const bLabel = regionToReadable[b.name]?.city || b.description || b.name;
    return aLabel.localeCompare(bLabel);
  });
};

export const CloudTabContent: React.FC<CloudTabContentProps> = ({
  instanceTypeObject,
  intendedInstanceType,
  intendedDiskStorage,
  instanceStorage,
  intendedCloudCredId,
  intendedFirewallRules,
  imageId,
  summaryWarning,
  onShowEditComputeModal,
  onCloudInstanceSelect,
  onStorageValidityChange,
  onIsOutOfStock,
  onProviderDifferenceChange,
  allInstancesAvailable,
  selectedLocation,
  onLocationChange,
}) => {
  const [isInstanceOutOfStock, setIsInstanceOutOfStock] = useState(false);
  const availableLocations = useMemo(
    () =>
      getAvailableLocationsForInstance(
        instanceTypeObject,
        allInstancesAvailable,
      ),
    [allInstancesAvailable, instanceTypeObject],
  );
  const handleOutOfStockChange = useCallback(
    (outOfStock: boolean) => {
      setIsInstanceOutOfStock(outOfStock);
      onIsOutOfStock?.(outOfStock);
    },
    [onIsOutOfStock],
  );
  const handleStorageChange = useCallback(
    (storage: string) => {
      if (instanceTypeObject) {
        onCloudInstanceSelect(instanceTypeObject, storage);
      }
    },
    [instanceTypeObject, onCloudInstanceSelect],
  );

  const cloudProviderContent = (
    <LaunchableMultiComputeSelector
      currentInstanceTypeObject={instanceTypeObject}
      intendedInstanceType={intendedInstanceType}
      intendedDiskStorage={intendedDiskStorage}
      intendedCloudCredId={intendedCloudCredId}
      setInstanceObject={onCloudInstanceSelect}
      intendedFirewallRules={intendedFirewallRules}
      onIsOutOfStock={handleOutOfStockChange}
      onProviderDifferenceChange={onProviderDifferenceChange}
      allowProviderSwitching={!imageId}
    />
  );

  return (
    <div className="flex flex-col">
      <div className="px-5 py-4">
        {summaryWarning && (
          <div className="mb-2 text-xs leading-5">{summaryWarning}</div>
        )}
        {isInstanceOutOfStock && (
          <div className="mb-2 flex items-center gap-1.5 text-red-500">
            <ExclamationTriangleIcon
              aria-hidden="true"
              className="w-4 h-4 shrink-0"
            />
            <p className="text-xs">
              This instance type is currently out of stock. Please select a
              different instance type or check back later.
            </p>
          </div>
        )}
        {instanceTypeObject ? (
          <LaunchableComputeOverview
            intendedInstanceType={intendedInstanceType}
            intendedDiskStorage={intendedDiskStorage}
            diskStorage={instanceStorage}
            instanceTypeObject={instanceTypeObject}
            cloudProviderContent={cloudProviderContent}
            onViewAllOptions={onShowEditComputeModal}
            onStorageChange={
              isInstanceOutOfStock ? undefined : handleStorageChange
            }
            onStorageValidityChange={onStorageValidityChange}
            availableLocations={availableLocations}
            selectedLocation={selectedLocation}
            onLocationChange={onLocationChange}
          />
        ) : (
          <div className="flex flex-col">
            <div className="mb-2">
              <button
                type="button"
                className="inline-flex items-center gap-1.5 rounded-md border border-zinc-700 bg-zinc-900 px-2.5 py-1.5 text-xs font-semibold text-highlight shadow-sm outline-none transition-colors hover:border-highlight/60 hover:bg-zinc-800 hover:text-highlightLighter focus-visible:ring-2 focus-visible:ring-highlight"
                onClick={onShowEditComputeModal}
              >
                Select compute
              </button>
            </div>
            <div className="flex items-center gap-2 text-red-500">
              <ExclamationTriangleIcon className="w-4 h-4 shrink-0" />
              <p className="text-xs font-medium">
                Default instance type unavailable. Please select a different instance type to proceed.
              </p>
            </div>
          </div>
        )}
        {isInstanceOutOfStock && (
          <div className="flex items-center gap-1.5 mt-2 text-red-500">
            <ExclamationTriangleIcon className="w-4 h-4 shrink-0" />
            <p className="text-xs">
              This instance type is currently out of stock. Please select a
              different instance type or check back later.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
